<script setup lang="ts">
import { FileDownloadSourceApplicationBuf } from "~/classes/generated/entity"

const props = defineProps({
  file: null,
  loading: { type: Boolean },
  error: null
})

const { file } = toRefs(props)
const { rendererState } = useRenderer({ file })
const text = ref("")
const didDownload = computed(() => isDefined(props.file.downloadSourceApplication()))
const didAgentReadWebsite = computed(() => file.value.downloadSourceApplication() === FileDownloadSourceApplicationBuf.AgentReadWebsite)
const collapsed = ref(didAgentReadWebsite.value)
const label = computed(() => {
  if (didAgentReadWebsite.value)
    return "Read website"
  else if (didDownload.value)
    return "Downloaded text"
  return "Wrote text"
})

watchImmediate(
  () => ({ objectUrl: rendererState.value.objectUrl }),
  async ({ objectUrl }) => {
    if (!objectUrl)
      return
    const response = await fetch(objectUrl)
    text.value = await response.text()
  },
)
</script>

<template>
  <agent-status-preview
    :file
    :loading
    :error
    :label
  >
    <div class="text-viewer">
      <div v-if="didAgentReadWebsite" class="header">
        <icon-action
          size="xs"
          type="link"
          class="link-icon"
        />
        <div class="file-name">
          {{ file.downloadSourceUrl() }}
        </div>
        <div class="collapse" @click="collapsed = !collapsed">
          <icon-action
            size="xs"
            :type="collapsed ? 'chevron-right' : 'chevron-down'"
            class="chevron-icon"
          />
        </div>
      </div>
      <div v-else class="header">
        <div class="file-name">
          {{ file.name() }}
        </div>
        <div class="collapse" @click="collapsed = !collapsed">
          <icon-action
            size="xs"
            :type="collapsed ? 'chevron-right' : 'chevron-down'"
            class="chevron-icon"
          />
        </div>
      </div>
      <template v-if="!collapsed">
        <interface-scrollbar vertical>
          <div class="body-text">
            {{ text }}
          </div>
        </interface-scrollbar>
      </template>
      <div v-else class="spacer h-0 flex-none" />
    </div>
  </agent-status-preview>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.text-viewer
  @apply w-full max-h-49 // place the bottom at a mid-text-boundary
  @apply shadow-card bg-[#07060866]
  @apply outline outline-0.75 outline-[#55575F40]
  @apply relative
  @apply flex flex-col gap-1
  @apply rounded-[6px]
  @apply pt-2
  @apply select-text

.header
  @apply h-[18px] flex-none flex items-center px-2 gap-1 w-full overflow-hidden
  .link-icon
    @apply flex-none

.file-name
  @apply t-menu-sm text-ellipses-inline min-w-0

.collapse
  @apply ml-auto rounded-[4px] cursor-pointer
  &:hover
    @apply bg-[#{$menu-bg-item-hover}]

.body-text
  @apply t-panel-md text-[#{$global-t-secondary}] px-2 whitespace-pre-wrap pb-2
</style>
